/**
 * OwlyLogo Component
 *
 * Logo reutilizable de OWLY con soporte para dark mode
 *
 * Dark Mode Behavior:
 * - Cuerpo y ojos internos: Negro → Blanco (se adaptan al tema)
 * - Iris naranja (#EB8734) → Se mantiene siempre igual
 *
 * Usage:
 * <OwlyLogo size="sm" />   // 40px
 * <OwlyLogo size="md" />   // 60px
 * <OwlyLogo size="lg" />   // 80px
 * <OwlyLogo size="xl" />   // 120px
 * <OwlyLogo width={100} /> // Custom size
 */

import { cn } from '@/lib/utils';

interface OwlyLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  width?: number;
  height?: number;
  className?: string;
}

const sizeMap = {
  sm: 40,
  md: 60,
  lg: 80,
  xl: 120,
};

export const OwlyLogo = ({ size = 'md', width, height, className }: OwlyLogoProps) => {
  const w = width || sizeMap[size];
  const h = height || (w * 132) / 85; // Mantener aspect ratio original

  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 85 132"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('owly-logo transition-colors duration-300', className)}
      aria-label="Owly Logo"
    >
      <g clipPath="url(#clip0_owly)">
        {/* Cabeza del búho - Negro → Blanco en dark mode */}
        <path
          d="M34.3823 24.0133C41.6833 28.832 47.4481 35.9358 51.2728 43.8575L51.2887 43.8334L51.3045 43.8575C55.1213 35.9358 60.894 28.832 68.195 24.0133C74.6566 19.7478 79.8988 18.1042 82.9237 10.5514C82.9554 10.4792 82.9792 10.399 83.0108 10.3188C83.0267 10.2787 83.0425 10.2387 83.0583 10.1906C83.09 10.1104 83.1217 10.0302 83.1454 9.942C83.1613 9.89389 83.1771 9.84578 83.2009 9.79768C83.2326 9.7175 83.2563 9.6293 83.288 9.54912C83.3038 9.50102 83.3197 9.45291 83.3355 9.39679C83.4305 9.10814 83.5176 8.8195 83.6047 8.52284C83.6206 8.46672 83.6364 8.41059 83.6522 8.35447C83.676 8.27429 83.6998 8.18609 83.7235 8.10591C83.7393 8.04177 83.7552 7.97763 83.771 7.91348C83.7948 7.83331 83.8106 7.75313 83.8344 7.67295C83.8502 7.60079 83.874 7.52061 83.8898 7.44845C83.9136 7.3362 83.9452 7.22395 83.969 7.1117C83.9927 7.0235 84.0086 6.92729 84.0323 6.83909C84.0482 6.77495 84.0561 6.71081 84.0719 6.63865C84.0878 6.55045 84.1036 6.47027 84.1194 6.38208C84.1353 6.31793 84.1432 6.25379 84.159 6.18965C84.1749 6.09344 84.1907 5.99722 84.2145 5.90101C84.2224 5.8529 84.2303 5.80479 84.2382 5.74867C84.262 5.60435 84.2857 5.46003 84.3095 5.32372C84.3095 5.29165 84.3174 5.25958 84.3253 5.22751C84.3412 5.11526 84.357 4.99499 84.3728 4.88274C84.3728 4.84265 84.3808 4.79455 84.3887 4.75446C84.4045 4.64221 84.4124 4.53797 84.4283 4.43374C84.4283 4.39365 84.4283 4.36158 84.4362 4.32149C84.4679 4.03285 84.4837 3.75223 84.4995 3.47962V3.41548C84.4995 3.30323 84.5075 3.19098 84.5154 3.07873V3.03062C84.5312 1.77984 84.3649 0.745537 83.9452 0.184288C82.021 -0.906138 78.521 2.78207 76.961 3.59187C74.8546 4.6823 72.6295 5.75669 70.7211 6.53442C65.3443 2.47739 58.7718 0.184288 51.2887 0.0560028C43.7976 0.192306 37.2331 2.47739 31.8721 6.55045C29.9637 5.77272 27.7386 4.69833 25.6322 3.60791C24.0723 2.7981 20.0179 -0.769835 18.0937 0.320591C16.9296 1.87605 18.2283 6.99143 19.6616 10.5514C22.6865 18.1042 27.9286 19.7478 34.3903 24.0133H34.3823Z"
          className="fill-gray-900 dark:fill-gray-50"
        />

        <g filter="url(#filter0_d_owly)">
          {/* Ojo izquierdo - Se adapta al tema */}
          <path
            d="M40.5344 37.1067C41.746 37.1067 42.8784 36.7459 43.8286 36.1205L35.3636 27.7418C34.7934 28.6799 34.4688 29.7784 34.4688 30.957C34.4688 34.3485 37.1849 37.1067 40.5424 37.1067H40.5344Z"
            className="fill-gray-900 dark:fill-gray-50"
          />
          {/* Parte naranja ojo izquierdo - usa style para evitar override de CSS global */}
          <path
            d="M44.0355 36.3771C43.9642 36.2969 43.9009 36.2087 43.8296 36.1286C42.8794 36.754 41.747 37.1148 40.5355 37.1148C37.1859 37.1148 34.4619 34.3646 34.4619 30.9651C34.4619 29.7864 34.7865 28.688 35.3567 27.7499C35.3567 27.7339 35.3725 27.7259 35.3804 27.7098C34.5806 27.0524 33.7571 26.4109 32.9177 25.7936C32.5297 25.4969 32.308 25.3365 32.308 25.3365C31.231 26.9401 30.6055 28.8804 30.6055 30.9571C30.6055 36.5054 35.0478 41.0034 40.5275 41.0034C42.2617 41.0034 43.893 40.5544 45.3104 39.7606C45.6192 39.5842 45.9281 39.3918 46.2131 39.1914C45.5084 38.2292 44.7799 37.2911 44.0276 36.3771H44.0355Z"
            style={{ fill: '#EB8734' }}
          />
        </g>

        <g filter="url(#filter1_d_owly)">
          {/* Ojo derecho - Se adapta al tema */}
          <path
            d="M68.149 30.965C68.149 29.7703 67.8164 28.6558 67.2305 27.7097L58.7812 36.1284C59.7315 36.7538 60.8639 37.1146 62.0754 37.1146C65.425 37.1146 68.149 34.3645 68.149 30.965Z"
            className="fill-gray-900 dark:fill-gray-50"
          />
        </g>

        <g filter="url(#filter2_d_owly)">
          {/* Parte naranja ojo derecho - usa style para evitar override de CSS global */}
          <path
            d="M70.3026 25.3365C69.2574 26.0982 68.2359 26.884 67.2381 27.7098C67.8162 28.6559 68.1567 29.7704 68.1567 30.9651C68.1567 34.3566 65.4406 37.1148 62.0831 37.1148C60.8715 37.1148 59.7391 36.754 58.7889 36.1286C58.7176 36.2087 58.6543 36.2969 58.583 36.3771C57.8307 37.2911 57.1022 38.2373 56.3975 39.1914C56.6905 39.3998 56.9914 39.5923 57.3002 39.7606C58.7176 40.5544 60.3489 41.0034 62.0831 41.0034C67.5628 41.0034 72.0051 36.5054 72.0051 30.9571C72.0051 28.8724 71.3796 26.9401 70.3026 25.3365V25.3365Z"
            style={{ fill: '#EB8734' }}
          />
        </g>

        {/* Pata izquierda - Negro → Blanco en dark mode */}
        <path
          d="M17.2147 129.809C27.4536 117.646 39.8463 98.395 44.7796 83.0008C51.9301 60.6711 30.8348 59.0515 25.9965 43.5851C25.0305 40.5062 24.0723 43.6813 23.3121 47.4337C23.2171 47.8987 23.13 48.3637 23.0429 48.8368C23.0112 48.9971 22.9795 49.1495 22.9478 49.3018C22.6549 50.6809 22.251 51.8194 21.7521 52.8056C21.6967 52.9179 21.6413 53.0221 21.5779 53.1263C21.4591 53.3348 21.3404 53.5432 21.2137 53.7437C21.0236 54.0404 20.8256 54.329 20.6198 54.6016C20.6118 54.6096 20.6039 54.6257 20.596 54.6337C20.1922 55.1629 19.7487 55.66 19.2815 56.141C19.2815 56.141 19.2577 56.1651 19.2498 56.1811C18.7905 56.6462 18.2996 57.1032 17.7928 57.5682C17.4444 57.8889 17.0801 58.2096 16.7079 58.5464C16.5021 58.7308 16.3041 58.9152 16.1061 59.0996L16.0507 59.1477C15.7577 59.4203 15.4726 59.6929 15.1955 59.9736C14.8391 60.3344 14.4907 60.6952 14.1502 61.064C10.3017 65.0569 7.57773 69.5869 5.57431 74.4618C3.54713 79.4008 -1.83755 98.9803 0.633068 103.607C0.878546 104.064 1.4091 104.264 2.54938 104.352V104.368C2.61273 104.328 2.67608 104.288 2.73151 104.248C2.79486 104.248 2.85821 104.264 2.92156 104.264C2.92156 104.312 2.9374 104.368 2.94532 104.424C2.99283 104.793 3.07201 105.298 3.16704 105.9C3.30166 106.734 3.48378 107.76 3.69759 108.874C3.91139 109.989 4.14895 111.183 4.41027 112.362C4.48154 112.699 4.56072 113.036 4.63991 113.364C5.06752 115.192 5.5268 116.9 5.98608 118.111C6.08902 118.384 6.17613 118.616 6.26323 118.808C6.34242 119.001 6.41369 119.161 6.48496 119.29C6.50871 119.33 6.53247 119.37 6.54831 119.41C6.57206 119.458 6.60374 119.498 6.62749 119.538C6.65917 119.586 6.69876 119.634 6.73043 119.666C6.75419 119.69 6.77003 119.706 6.79378 119.722C6.82546 119.755 6.85713 119.771 6.89673 119.795C6.91256 119.795 6.9284 119.811 6.93632 119.819C6.99175 119.843 7.0551 119.867 7.11845 119.875C7.15804 119.883 7.20555 119.891 7.25307 119.899C7.43519 119.923 7.65692 119.955 7.96575 120.051C8.01326 120.067 8.06869 120.083 8.1162 120.099C9.39902 123.86 12.3527 135.606 17.2147 129.825V129.809Z"
          className="fill-gray-900 dark:fill-gray-50"
        />

        {/* Pata derecha - Negro → Blanco en dark mode */}
        <path
          d="M50.5138 131.172C52.4856 133.802 66.5095 117.037 73.2404 96.3345C74.737 91.7403 75.8694 86.9456 76.4316 82.1349C76.8117 78.8797 76.8354 76.0574 76.6058 73.5799C75.5368 61.7937 68.8772 57.913 68.9485 52.1322C68.9722 49.9994 69.7641 45.0444 67.5865 51.8195C65.7968 57.3838 75.7585 60.7674 75.2596 76.0975C75.1963 78.0538 74.9666 80.1946 74.5153 82.5679C72.3139 94.2018 64.712 102.436 55.2413 113.34C53.515 115.329 51.7254 117.413 49.8883 119.618C49.033 120.645 48.9618 121.494 48.6609 123.258C48.5025 124.205 48.3995 125.207 48.5183 125.84C48.6371 126.482 48.9855 126.754 49.7299 126.233C49.9437 126.081 50.1496 125.88 50.403 125.696C50.5851 125.56 50.791 125.431 51.0602 125.327C51.1156 128.622 49.7853 130.346 50.5217 131.172H50.5138Z"
          className="fill-gray-900 dark:fill-gray-50"
        />
      </g>

      <defs>
        <filter
          id="filter0_d_owly"
          x="26.6055"
          y="21.3365"
          width="23.6074"
          height="23.6669"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_owly" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_owly" result="shape" />
        </filter>
        <filter
          id="filter1_d_owly"
          x="54.7812"
          y="23.7097"
          width="17.3682"
          height="17.4049"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_owly" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_owly" result="shape" />
        </filter>
        <filter
          id="filter2_d_owly"
          x="52.3975"
          y="21.3365"
          width="23.6074"
          height="23.6669"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_owly" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_owly" result="shape" />
        </filter>
        <clipPath id="clip0_owly">
          <rect width="84.5" height="131.444" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
